/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  FormStateBuilder,
  WebSocketService,
} from '@gh/builder-shared';
import { TranslationService, TranslationTupleOrId } from '@gh/core-mls';
import { NavTabBarComponent } from '@gh/common-internal-shared';
import {
  ActionFlow,
  AppRouter,
  EnumDictionary,
  getParentRouteUrl,
  getRouteInitData,
  getRouteParentTitle,
  IS_REGION_AU_OR_NZ,
  LogicalAction,
  queryAll,
  queryRecalculationNeeded,
  TextDictionary,
} from '@gh/common-shared';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { isEmpty } from 'lodash';
import {
  DialogService,
  FormMode,
  NotificationService,
  WaitingContext,
  WaitingMode,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  AutoSaveMin,
  BuilderPermission,
  HlpDesignImage,
  HlpDetails,
  HlpStatus,
  ListingStatus,
  Storeys,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { HlpDetailsBusinessService } from '../../resources/hlp-details-business.service';
import { HlpDetailsFormState } from './hlp-details-form.service';
import { filter$, map$ } from '@gh/rx/operators';
import { queryQuoteHasErrors, QuoteDetailsStateEventType } from './hlp-details-quote-tab/hlp-quote-details.state';
import { queryQuoteVariations } from '../../../leads/components/quote-details/quote-details.state';
import { QuoteSaveFlowState } from '../../../leads/components/quote-details/quote-details-command.service';
import { Observable } from 'rxjs';
import { of$ } from '@gh/rx';
import { hlpQuoteDetailsInitFromDto } from '../../resources/hlp-quote.model';
import { MessageSeverity } from '@gh/core-messages';

// TODO WILL BE REFACTOR
@Component({
  selector:    'gh-hlp-details-tab-container',
  template: require('./hlp-details-tab-container.component.html'),
  styles:   [require('./hlp-details-tab-container.component.scss')],
  providers:   [HlpDetailsFormState],
})
export class HlpDetailsTabContainerComponent extends AbstractFormComponent<HlpDetails> implements OnInit, OnDestroy, AfterViewInit {
  currentTab: string;
  recalculationNeeded$: Observable<boolean>;
  hlpStatus = HlpStatus;
  formModes = FormMode;
  formMode: FormMode;
  isAutoSaveOn: boolean;
  isShowContent: boolean;
  autoSaveDuration: any;
  interval: any;
  connectionTimer: any;
  listingStatuses: EnumDictionary<ListingStatus>;
  landAreaUomList: TextDictionary;
  storeys: EnumDictionary<Storeys>;
  autoSaveMin: EnumDictionary<AutoSaveMin>;
  waitingProcess: any;
  @ViewChild(NavTabBarComponent, { static: true }) navigation: NavTabBarComponent;
  tabs = {
    Details: 'DETAILS',
    Design:  'DESIGN',
    Quote:   'QUOTE',
  };

  constructor(
    private translations: TranslationService,
    private route: ActivatedRoute,
    public form: HlpDetailsFormState,
    public userContext: BuilderUserContext,
    public businessService: HlpDetailsBusinessService,
    private formStateBuilder: FormStateBuilder,
    private router: AppRouter,
    private angRouter: Router,
    private commonActions: CommonActions,
    private service: HlpDetailsBusinessService,
    private webSocket: WebSocketService,
    private notifications: NotificationService,
    private navigationService: BuilderNavigationService,
    private pipelines: FormActionPipelines,
    private dialogService: DialogService,
    private waitingContext: WaitingContext,
  ) {
    super();
    this.isShowContent = true;
    this.isAutoSaveOn = true;
  }

  get parenRoute(): string {
    return '/sales/hlp';
  }

  get parentTitle(): TranslationTupleOrId {
    return getRouteParentTitle(this.route) || 'lbl_hlps';
  }

  get isPublishVisible(): boolean {
    return this.formMode !== FormMode.CreateMode && !this.businessService.hlpDetails.commonDetails.publishedDate &&
      (
        this.userContext.hasSystemRole(SystemRoleBit.Corporate)
        || this.userContext.hasSystemRole(SystemRoleBit.Master)
        || this.userContext.hasPermission(BuilderPermission.Sales.Hlp.EDIT)
      );
  }

  get isUnPublishVisible(): boolean {
    return !!this.businessService.hlpDetails.commonDetails.publishedDate && this.businessService.hlpDetails.status !== HlpStatus.Deleted &&
      (
        this.userContext.hasSystemRole(SystemRoleBit.Corporate)
        || this.userContext.hasSystemRole(SystemRoleBit.Master)
        || this.userContext.hasPermission(BuilderPermission.Sales.Hlp.EDIT)
      );
  }

  get isUnPublishActive(): boolean {
    return !!this.businessService.hlpDetails.commonDetails.publishedDate && this.businessService.hlpDetails.status === HlpStatus.Active &&
      (
        this.userContext.hasSystemRole(SystemRoleBit.Corporate)
        || this.userContext.hasSystemRole(SystemRoleBit.Master)
        || this.userContext.hasPermission(BuilderPermission.Sales.Hlp.EDIT)
      );
  }
  get showHLPNumber(): boolean {
    return this.formMode !== this.formModes.CreateMode && this.businessService.hlpDetails.status === HlpStatus.Active;
  }

  ngOnInit(): void {
    const { details, listingStatuses, landAreaUom, autoSaveMin, tinymce, storeys, tabName } = getRouteInitData(this.route);
    this.businessService.tinymce = tinymce;
    this.formMode = this.businessService.getFormMode(details);
    this.businessService.hlpDetails = details;
    this.businessService.snapshotVariations = [...details.variations];
    this.listingStatuses = listingStatuses;
    this.landAreaUomList = landAreaUom;
    this.storeys = storeys;
    this.autoSaveMin = autoSaveMin;
    this.form.build({...details});
    this.businessService.mainForm = this.form;
    // need for GoTo
    this.businessService.onProcess = () => this.processFlow;
    this.autoSaveDuration = '30';
    this.recalculationNeeded$ = of$(false);
    this.startAutoSave();
    this.businessService.needValidate = false;
    this.currentTab = this.isTabExist(tabName) ? tabName : this.tabs.Details;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.connectToWS();
      // tslint:disable-next-line:no-magic-numbers
    },         100);
  }

  ngOnDestroy(): void {
    this.disconnectWS();
    this.stopSpinner();
    clearInterval(this.connectionTimer);
    clearInterval(this.interval);
    // @ts-ignore
    this.businessService.masterInfoForm = undefined;
    // @ts-ignore
    this.businessService.mainForm = undefined;
    // @ts-ignore
    this.businessService.hlpDetails = undefined;
    this.businessService.quoteForm = undefined;
  }

  startAutoSave(): void {
    this.interval = setInterval(() => {
      if (!this.businessService.mainForm.dirty) {
        return;
      }
      this.dialogService.confirmation(
        yesNoConfirmation('msg_unsaved_info')).pipe(
        filter$(yesAnswer)).subscribe(() => {
        this.onProcess();
      });
      // tslint:disable-next-line:no-magic-numbers
    },                          this.autoSaveDuration * 60 * 1000);
  }

  setAutoSaveDuration(event: any): void {
    this.autoSaveDuration = event;
    clearInterval(this.interval);
    this.startAutoSave();
  }

  onTabClick(name: string): void {
    if (this.currentTab !== name) {
      this.currentTab = name;
    }
    const queryParams: Params = { tab: name };

    this.angRouter.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      },
    );
  }

  isBackRouterActive(): boolean {
    return !isEmpty(getParentRouteUrl(this.route));
  }

  goToAel(): void {
    const navigationParams = {
      parentTitle: 'lbl_hl',
      queryParams: {
        label:      'lbl_hlps',
        entityType: 'HLP',
        entityId:   this.businessService.hlpDetails.houseAndLandPackageId,
      },
    };
    this.router.navigateToChild([`/ael`], navigationParams);
  }

  get completeFlow(): ActionFlow<FormFlowState<HlpDetails>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        () => !this.businessService.isAllValid(),
        () => this.handleErrors(),
      ),
      execute:      this.saveAction(),
      finish:       (state: FormFlowState<HlpDetails>) => {
      },
      error:        (resp) => {
        this.notifications.processAll(resp.error);
        this.isShowContent = true;
        this.businessService.isShowQuoteTab = true;
      },
    });
  }

  saveAction(): LogicalAction<FormFlowState<HlpDetails>> {
    const data = this.businessService.prepearData();
    if (data.commonDetails.displayPrice < data.totalPrice1 && this.businessService.mainForm.dirty) {
      this.notifications.warning('msg_display_price_lower');
    }
    return this.commonActions.async((state: FormFlowState<HlpDetails>) => {
      this.isShowContent = false;
      this.businessService.isShowQuoteTab = false;
      if (this.businessService.hlpDetails.houseAndLandPackageId) {
        return this.service.update(this.businessService.prepearData()).pipe(map$((saveResponse) => {
          delete this.businessService.masterInfoForm;
          delete this.businessService.quoteForm;
          this.setResult(saveResponse);
          return state;
        }));
      }
      return this.service.create(this.businessService.prepearData()).pipe(map$((saveResponse) => {
          this.isShowContent = true;
          this.businessService.isShowQuoteTab = true;
          this.router.switchTo(['/sales/hlp', saveResponse.houseAndLandPackageId], {
            omitQueryParams: ['clonedHlp'],
          });
          return state;
        },
      ));
    });
  }

  recalculateFlow(): ActionFlow<QuoteSaveFlowState> {
    return this.pipelines.recalculate({
      validateForm: this.commonActions.validateWithEffect(
        () => this.businessService.quoteForm.state.project(queryQuoteHasErrors),
        () => this.handleErrors(),
      ),
      execute:      this.commonActions.async((state: QuoteSaveFlowState) =>
        this.service.calculate(this.businessService.prepearData()).pipe(
          map$(({ saveResponse, response }) => {
              this.businessService.mainForm.get('commonDetails.displayPrice').setValue(response.displayPrice);
              this.businessService.hlpDetails.totalPrice1 = response.totalPrice1;
              this.businessService.hlpDetails.totalPrice2 = response.totalPrice2;
              return { ...state, saveResponse };
            },
          ))),
      finish:       (state: QuoteSaveFlowState) => {
        const { saveResponse } = state;
        if (saveResponse) {
          this.recalculationNeeded$ = of$(false);
          return this.businessService.quoteForm.state.mutate({ type: QuoteDetailsStateEventType.MergeOnRecalculate, saveResponse });
        }
      },
      error:        (resp) => {
        this.notifications.processAll(resp.error);
      },
    });
  }

  onProcess(): any {
    this.businessService.needValidate = true;
    return this.processFlow.run({
      trigger: 'process',
      form:    this.businessService.mainForm,
      value:   this.businessService.mainForm.submitValue,
    });
  }

  onRecalculate(): void {
    const flow = this.recalculateFlow();

    flow.run({
      form:  this.businessService.quoteForm,
      value: this.businessService.quoteForm.state.project(queryAll),
    });
  }

  setResult(details: HlpDetails): void {
    this.businessService.hlpDetails = { ...details };
    this.businessService.snapshotVariations = [...details.variations];
    this.businessService.mainForm.build({ ...details });
    if (this.businessService.quoteForm && this.businessService.quoteForm.state) {
      this.businessService.quoteForm.state.mutate({
        type:         QuoteDetailsStateEventType.MergeOnSave,
        saveResponse: hlpQuoteDetailsInitFromDto(this.businessService.hlpQuoteDetailsInitFromHlpDetailsDto(details)),
      });
    }
    setTimeout(() => {
      this.isShowContent = true;
      this.businessService.isShowQuoteTab = true;
      // tslint:disable-next-line:no-magic-numbers
    },         100);
    this.recalculationNeeded$ = of$(false);
  }

  setAutoSave(event: any): void {
    this.isAutoSaveOn = event.checked;
    if (!this.isAutoSaveOn) {
      clearInterval(this.interval);
    }
  }

  onQuoteChange(): void {
    this.businessService.needValidate = false;
    this.businessService.hlpDetails.variations = this.businessService.quoteForm.state.project(queryQuoteVariations);
    setTimeout(() => {
      this.recalculationNeeded$ = this.businessService.quoteForm.state.select(queryRecalculationNeeded);
      // tslint:disable-next-line:no-magic-numbers
    },         100);
    this.businessService.mainForm.getControl('quoteHasChanges')
      .setValue(!this.businessService.mainForm.getControl('quoteHasChanges').value);
    this.businessService.mainForm.getControl('quoteHasChanges').markAsDirty();
  }

  onQuoteLeave(event: any): void {
    this.businessService.hlpDetails = {
      ...this.businessService.hlpDetails,
      ...event,
    };
  }

  validate(): void {
    if (!this.businessService.isAllValid()) {
      this.handleErrors();
    }
  }

  onPublish(): void {
    if (this.businessService.mainForm.dirty) {
      this.dialogService.message({
          messageId: 'msg_hlp_publish_process',
          severity: MessageSeverity.Warning,
          panelClass: 'cdk-overlay-strict',
      });
      return;
    }
    this.validate();
    if (this.businessService.isAllValid()) {
      this.isShowContent = false;
      this.businessService.isShowQuoteTab = false;
      this.waitingProcess = this.waitingContext.start(WaitingMode.Blocked);
      this.businessService.publish(this.businessService.hlpDetails.houseAndLandPackageId).subscribe(
        (response) => {
          if (this.webSocket.hasWSConnect()) {
            return;
          }
          this.stopSpinner();
          this.setResult(response);
          // tslint:disable-next-line:align
        }, (error) => {
          this.businessService.isShowQuoteTab = true;
          this.isShowContent = true;
          this.stopSpinner();
          this.notifications.processAll(error);
        });
    }
  }

  onUnPublish(): void {
    if (!this.isUnPublishActive) {
      return;
    }
    this.dialogService.confirmation(
      yesNoConfirmation('msg_hlp_unpublish')).pipe(
      filter$(yesAnswer)).subscribe(() => {
      this.isShowContent = false;
      this.businessService.isShowQuoteTab = false;
      this.waitingProcess = this.waitingContext.start(WaitingMode.Blocked);
      this.businessService.unPublish(this.businessService.hlpDetails.houseAndLandPackageId).subscribe(
        (response) => {
          if (this.webSocket.hasWSConnect()) {
            return;
          }
          this.stopSpinner();
          this.setResult(response);
          // tslint:disable-next-line:align
        }, (error) => {
          this.stopSpinner();
          this.businessService.isShowQuoteTab = true;
          this.isShowContent = true;
          this.notifications.processAll(error); });
    });
  }

  setLockIfSyncInProgress(): void {
    this.connectionTimer = setTimeout(() => {
      if (this.businessService.hlpDetails.isSyncInProgress) {
        if (this.webSocket.hasWSConnect()) {
          this.waitingProcess = this.waitingContext.start(WaitingMode.Blocked);
          return;
        }

        if (!this.webSocket.failConnected) {
          this.setLockIfSyncInProgress();
        }
      }
      // tslint:disable-next-line:no-magic-numbers
    },                                200);
  }

  getFormattedDate(timeStamp: string): string {
    const date = new Date(timeStamp);
    if (IS_REGION_AU_OR_NZ) {
      return `${ date.getDate() }/${ date.getMonth() + 1 }/${ date.getFullYear() }`;
    }
    return `${ date.getMonth() + 1 }/${ date.getDate() }/${ date.getFullYear() }`;
  }

  private handleErrors(): void {
    if (this.businessService.quoteForm && this.businessService.quoteForm.state) {
      this.businessService.quoteForm.state.mutate({ type: QuoteDetailsStateEventType.NavigateToInvalid });
    }

    this.businessService.mainForm.markAllAsTouched();
    if (this.businessService.mainForm.invalid) {
      // tslint:disable-next-line:forin
      const commonDetails = this.businessService.mainForm.get('commonDetails');
      const designDetails = this.businessService.mainForm.get('designDetails');
      if (commonDetails.errors || commonDetails.invalid) {
        this.currentTab = this.tabs.Details;
        return;
      }
      if (designDetails.errors || designDetails.invalid) {
        if (designDetails.get('websiteDescriptionText')?.errors?.maxlength) {
          this.notifications.error('msg_field_maxlength', {
            requiredLength: '2500',
          });
        }
        this.currentTab = this.tabs.Design;
      }
      return;
    }
    if (!this.businessService.mainForm.get('designDetails.facades')?.value.find((image: HlpDesignImage) => image.isSelected)) {
      this.notifications.error('msg_facade_required');
      this.currentTab = this.tabs.Design;
      return;
    }
    this.currentTab = this.tabs.Quote;
    setTimeout(() => {
      if (this.businessService.masterInfoFormChangeDetectorRef && this.businessService.masterInfoForm) {
        this.businessService.masterInfoForm.group.markAllAsTouched();
        this.businessService.masterInfoFormChangeDetectorRef.detectChanges();
      }
      // tslint:disable-next-line:no-magic-numbers
    },         100);

  }

  private disconnectWS(): void {
    if (this.webSocket && this.webSocket.subscriptions$ && this.webSocket.hasWSConnect()
      && this.webSocket.subscriptions$[`/ws/topic/hlp-sync-finished/${this.businessService.hlpDetails.houseAndLandPackageId}`]) {
      this.webSocket.subscriptions$[`/ws/topic/hlp-sync-finished/${this.businessService.hlpDetails.houseAndLandPackageId}`].unsubscribe();
    }
  }

  private connectToWS(): void {
    if (!this.businessService.hlpDetails.houseAndLandPackageId) {
      return;
    }

    this.setLockIfSyncInProgress();
    this.webSocket.on(`/ws/topic/hlp-sync-finished/${this.businessService.hlpDetails.houseAndLandPackageId}`, (res: any) => {
      this.stopSpinner();
      if (!res.body) {
        return;
      }
      const message = JSON.parse(res.body);
      if (!message.syncSuccess) {
        this.notifications.error('msg_integration_error');
      }
      if (!this.businessService.hlpDetails.houseAndLandPackageId) {
        return;
      }
      this.isShowContent = false;
      this.businessService.isShowQuoteTab = false;
      this.businessService.get(this.businessService.hlpDetails.houseAndLandPackageId).subscribe((response) => {
        this.setResult(response);
      });

    });
  }

  private stopSpinner(): void {
    if (this.waitingProcess) {
      this.waitingProcess.stop();
    }
  }

  private isTabExist(tabName: string): boolean {
    if (!tabName || !tabName.length) {
      return false;
    }
    let isExist = false;
    for (const tab in this.tabs) {
      if (this.tabs[tab] === tabName) {
        isExist = true;
      }
    }
    return isExist;
  }
}
